import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import {
  OnboardingProgress,
  OnboardingProgressUpdateRequest,
  UserPreferences,
  UserPreferencesUpdateRequest,
} from 'src/types/TOnboarding';
import {
  updateOnboardingProgress,
  updateUserPreferences,
  loadDemoSamples,
  LoadDemoSamplesRequest,
  LoadDemoSamplesResponse,
} from '../functions/onboardingApi';
import { queryNames } from '../utils/queryNames';

export function useUpdateOnboardingProgressMutation(
  mutationOptions?: UseMutationOptions<OnboardingProgress, FetchError, OnboardingProgressUpdateRequest>,
) {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const mutationFn = async (data: OnboardingProgressUpdateRequest) => {
    const result = await updateOnboardingProgress(data, accessToken);
    if ('error' in result) {
      throw result;
    }
    return result as OnboardingProgress;
  };

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData([queryNames.onboardingProgress], data);
      mutationOptions?.onSuccess?.(data, variables, context);
    },
  });
}

export function useUpdateUserPreferencesMutation(
  mutationOptions?: UseMutationOptions<UserPreferences, FetchError, UserPreferencesUpdateRequest>,
) {
  const accessToken = useAccessToken();

  const mutationFn = async (data: UserPreferencesUpdateRequest) => {
    const result = await updateUserPreferences(data, accessToken);
    if ('error' in result) {
      throw result;
    }
    return result as UserPreferences;
  };

  return useMutation(mutationFn, mutationOptions);
}

export function useLoadDemoSamplesMutation(
  mutationOptions?: UseMutationOptions<LoadDemoSamplesResponse, FetchError, LoadDemoSamplesRequest>,
) {
  const accessToken = useAccessToken();

  const mutationFn = async (data: LoadDemoSamplesRequest) => {
    const result = await loadDemoSamples(data, accessToken);
    if ('error' in result) {
      throw result;
    }
    return result as LoadDemoSamplesResponse;
  };

  return useMutation(mutationFn, mutationOptions);
}
